import parse from "html-react-parser";
import {
  charterApi,
  destinationsApi,
  hotelsApi,
  staticApi,
  statisticsApi,
  toursApi,
} from "src/services/api";
import { isLangAbsent, redirectWithLang } from "./utils/loader-utils";
import { STATIC_ELEMENT } from "src/constants/static";
import { TOUR_STATUS } from "src/constants";
import { nanoid } from "@reduxjs/toolkit";
import { buildLinkToCheapFlightsOfferPage } from "src/utils/query-utils";
import { getDisplayDateValue, FLIGHT_DATE_FORMAT } from "src/utils/date-utils";

const MAX_CHEAP_TOURS = 10;

function isPastDeparture(departureDate) {
  if (!departureDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(departureDate) < today;
}

// One card per country (the cheapest tour there) instead of raw cheapest-overall — otherwise a
// country with lots of cheap routes could fill the whole carousel and crowd out everywhere else.
function pickCheapestPerCountry(tours) {
  const cheapestByCountry = new Map();
  for (const tour of tours) {
    const existing = cheapestByCountry.get(tour.country);
    if (!existing || tour.price < existing.price) {
      cheapestByCountry.set(tour.country, tour);
    }
  }
  return Array.from(cheapestByCountry.values());
}

export const HEADING_ID_PREFIX = "cheap-flights-booking-guide-";
export const MAX_CHEAP_FLIGHTS = 21; // D5: max 21 cards = 3 columns x 7 rows on desktop

/**
 * D5: map destination payloads into home-page cards.
 * Offer-cache DTOs carry authoritative per-person prices; legacy payloads use
 * dest.city/dest.iata/price. Pure, exported for unit tests.
 */
export const mapDestinationCards = (items, isOffersSource) =>
  items.map((item) =>
    isOffersSource
      ? {
          id: nanoid(5),
          city: item.destinationName,
          iata: item.destinationId,
          price: item.pricePerPerson,
          currency: item.currency,
          link: buildLinkToCheapFlightsOfferPage("ow", { dest: { iata: item.destinationId } }),
          direct: false,
        }
      : {
          id: nanoid(5),
          // Legacy BestPriceOfferEntity returns city/iata at the TOP level
          // (no `dest` sub-object). Tolerate both shapes and never crash when
          // a destination object is missing.
          city: item.city ?? item.dest?.city,
          iata: item.iata ?? item.dest?.iata,
          price: item.price,
          link: buildLinkToCheapFlightsOfferPage("ow", {
            dest: { iata: item.iata ?? item.dest?.iata },
          }),
          direct: item.stopNum === 0,
        }
  );

export const parseHeadingsFromHtml = (htmlContent, minLevel = 4, maxLevel = 4) => {
  // Extract headings during render (works in SSR)
  const headings = [];
  let count = 1;

  const options = {
    replace: (domNode) => {
      if (domNode.type === "tag" && /^h[1-6]$/.test(domNode.name)) {
        const level = parseInt(domNode.name.substring(1));
        if (level >= minLevel && level <= maxLevel) {
          const anchor =
            domNode.attribs?.id ||
            domNode.children[0]?.data
              ?.toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^\w-]/g, "");

          if (anchor && domNode.children[0]?.data) {
            headings.push({
              anchor: HEADING_ID_PREFIX + count++,
              title: domNode.children[0].data,
              level,
            });
          }
        }
      }
    },
  };

  parse(htmlContent, options);
  return headings;
};

function generateHotelPreviewLink(hid, guests, s_iata, d_iata, start, end, searchRequestKey, lang) {
  return (
    `/search/hotels/preview/${hid}?guests=${guests.join(",")}` +
    `&src=${s_iata}&dest=${d_iata}&start=${start}&end=${end}` +
    `&srkey=${searchRequestKey}&lang=${lang}`
  );
}

export async function loader({ request, params }) {
  if (isLangAbsent(request)) return redirectWithLang(request);
  const lang = new URL(request.url).searchParams.get("lang");

  const loaderData = {
    saleBanners: {
      data: Array.from({ length: 3 }, () => ({ id: nanoid(5), image: null })),
      error: false,
    },
    destinations: { data: [], error: false },
    selectedDeals: { data: [], error: false },
    topDestinations: { data: [], error: false },
    cheapDestinations: { data: [], error: false },
    bookingGuide: { data: { css: null, milestones: [], html: null }, error: false },
    cheapHotels: { data: [], error: false },
    cheapTours: { data: [], error: false },
    charterDeals: { data: [], error: false },
  };

  const blockingApiCalls = [];
  // load sale banners
  blockingApiCalls.push(
    staticApi.getStaticBanners(lang).then((res) => ({
      data: res.data.map((item) => ({ image: item, id: nanoid(5), link: "/" })),
    }))
  );

  // load cached cheap flights (categories + matchingDeal lookup)
  blockingApiCalls.push(
    statisticsApi
      .getCheapFlights(
        "oneway",
        window.SERVER_DATA.REACT_APP_DEFAULT_ORIGIN_IATA,
        ...Array(5).fill(null),
        true
      )
      .catch(console.error)
  );

  // load top destinations: durable package offer cache first, legacy catalog as fallback
  blockingApiCalls.push(
    destinationsApi
      .getTravelDeals(window.SERVER_DATA.REACT_APP_TENANT_NAME)
      .then((res) => ({ data: res.data, source: "offers" }))
      .catch(() =>
        destinationsApi
          .getLegacyTravelDeals()
          .then((res) => ({ data: res.data, source: "legacy" }))
          .catch((err) => {
            console.error(err);
            throw err;
          })
      )
  );

  const company = window.SERVER_DATA.REACT_APP_TENANT_NAME;
  //load static HTML file (booking process info/roadmap)
  blockingApiCalls.push(
    staticApi
      .getStaticFiles(company, lang, STATIC_ELEMENT.booking_guide)
      .then((res) => ({ data: { ...res, milestones: parseHeadingsFromHtml(res.html) } }))
  );

  // load cached cheap hotel deals (same endpoint/mapping as the Hotels page carousel)
  blockingApiCalls.push(
    hotelsApi.getCheapHotels().then((res) => ({
      data: res.data
        // Drop entries too incomplete to render a meaningful card (no name or no way to link
        // to it) instead of rendering a broken/blank one. Price is allowed to be missing —
        // CompactHotelCard already falls back to "--" for that.
        .filter((item) => item.name && (item.link || item.hid))
        .map((item) => ({
          id: item.hid || nanoid(5),
          name: item.name,
          city: item.city,
          iata: item.iata,
          pricePerNight: item.pricePerNight,
          totalPrice: item.totalPrice,
          rating: item.rating,
          reviews: item.reviews,
          amenities: item.amenities,
          remainingRooms: item.remainingRooms,
          location: item.location,
          // item.start/end are raw ISO dates — display them as "DD.MM.YYYY" (same format the
          // bus-tour cards already use), not the raw "YYYY-MM-DD" that was showing before.
          dates:
            item.dates ||
            (item.start && item.end
              ? `${getDisplayDateValue(item.start, FLIGHT_DATE_FORMAT)} – ${getDisplayDateValue(item.end, FLIGHT_DATE_FORMAT)}`
              : undefined),
          guests: item.guests,
          discount: item.discount,
          link:
            item.hid && item.searchRequestKey && Array.isArray(item.guests)
              ? generateHotelPreviewLink(
                  item.hid,
                  item.guests,
                  item.sourceIata,
                  item.iata,
                  item.start,
                  item.end,
                  item.searchRequestKey,
                  lang
                )
              : item.link,
          image: item.image,
        })),
    }))
  );

  // load cheapest upcoming bus tours (same source as the tours catalog page)
  blockingApiCalls.push(
    toursApi.getTours().then((res) => {
      const tours = Array.isArray(res.data) ? res.data : [];
      const eligible = tours.filter(
        (tour) => tour.status !== TOUR_STATUS.cancelled && !isPastDeparture(tour.departureDate)
      );
      return {
        data: pickCheapestPerCountry(eligible)
          .sort((a, b) => a.price - b.price)
          .slice(0, MAX_CHEAP_TOURS),
      };
    })
  );

  // load admin-managed charter deals (Atpūtas ceļojumi carousel) — the backend for this
  // endpoint doesn't exist in every environment yet, so an empty/failed response just means
  // "nothing admin-managed yet" rather than a real error; CharterDealsCarousel falls back to
  // its own hardcoded placeholder list in that case (see charterDeals.js).
  blockingApiCalls.push(
    charterApi
      .getCharterDeals()
      .then((res) => ({ data: Array.isArray(res.data) ? res.data : [] }))
      .catch(() => ({ data: [] }))
  );

  await Promise.allSettled(blockingApiCalls)
    .then((results) => {
      results.forEach((result, i) => {
        const key = [
          "saleBanners",
          "selectedDeals",
          "destinations",
          "bookingGuide",
          "cheapHotels",
          "cheapTours",
          "charterDeals",
        ][i];
        if (result.status === "rejected") {
          console.error(result.reason);
          loaderData[key].error = true;
        } else if (result.status === "fulfilled") {
          loaderData[key].data = result.value.data;
          if (key === "destinations") loaderData.destinations.source = result.value.source;
        }
      });
    })
    .catch(console.error);

  if (loaderData.selectedDeals.error) {
    loaderData.topDestinations.error = true;
  }
  if (loaderData.destinations.error) {
    loaderData.topDestinations.error = true;
    loaderData.cheapDestinations.error = true;
  }

  if (loaderData.destinations.data?.length) {
    const isOffersSource = loaderData.destinations.source === "offers";
    const { topDestinations, cheapDestinations } = mapDestinationCards(
      loaderData.destinations.data,
      isOffersSource
    ).reduce(
        (acc, item) => {
          let targetArr = acc.cheapDestinations;
          const matchingDeal = loaderData.selectedDeals.data.find((d) => d.iata === item.iata);
          if (matchingDeal) {
            targetArr = acc.topDestinations;
            item.category = matchingDeal.category;
          }

          if (!matchingDeal && targetArr.length >= MAX_CHEAP_FLIGHTS) return acc;

          const hasDuplicate = targetArr.some((i) => i.iata === item.iata);
          const cheaperNewItemIndex = targetArr.findIndex(
            (i) => i.iata === item.iata && i.price > item.price
          );

          if (cheaperNewItemIndex >= 0) {
            targetArr[cheaperNewItemIndex] = item;
          } else if (!hasDuplicate) {
            targetArr.push(item);
          }
          return acc;
        },
        { topDestinations: [], cheapDestinations: [] }
      );
    loaderData.topDestinations.data = topDestinations;
    loaderData.cheapDestinations.data = cheapDestinations;
  }
  delete loaderData.destinations;
  delete loaderData.selectedDeals;

  return loaderData;
}
